/**
 * TransformEventBus - Simplified Singleton
 *
 * Sistema di eventi basato su SharedValue singleton per comunicazione zero-latency
 * tra controlli globali (slider) e provider locali (UserImageTransformProvider).
 *
 * Tutti gli eventi viaggiano nel UI thread utilizzando React Native Reanimated.
 */
import { makeMutable } from 'react-native-reanimated';

/**
 * Tipi di eventi supportati dal sistema
 */
export type TransformEventType =
  | 'temp-zoom'      // Zoom temporaneo durante slide
  | 'temp-rotation'  // Rotazione temporanea durante slide
  | 'temp-pan'       // Pan temporaneo durante gesture
  | 'apply-zoom'          // Applica tutte le trasformazioni temporanee
  | 'apply-rotation'          // Applica tutte le trasformazioni temporanee
  | 'apply-pan'          // Applica tutte le trasformazioni temporanee
  | 'reset';         // Reset stati temporanei

/**
 * Struttura dati evento
 */
export interface TransformEventData {
  id: number;                                                   // ID univoco auto-incrementante
  type: TransformEventType;
  userImageId: string;
  payload: any;
  source: 'slider' | 'gesture' | 'system' | 'keyboard';        // Per debugging/analytics
  timestamp: number;                                           // Timestamp di creazione evento (Date.now())
}


/**
 * Singleton SharedValue per gli eventi
 * Inizializzato subito per evitare problemi di lazy initialization
 */
export const eventSharedValue = makeMutable<TransformEventData | null>(null);

/**
 * Contatore auto-incrementante per ID univoci
 */
let eventIdCounter = 0;

/**
 * Emit evento (worklet-safe, funziona sia da UI che JS thread)
 *
 * @param event - Evento da emettere (senza id e timestamp)
 */
export const emit = (event: Omit<TransformEventData, 'id' | 'timestamp'>) => {
  'worklet';

  // Genera ID univoco auto-incrementante e timestamp
  eventIdCounter++;
  const eventWithId: TransformEventData = {
    ...event,
    id: eventIdCounter,
    timestamp: Date.now(),
  };

  // Debug log (worklet-safe)
  if (__DEV__) {
    console.log('🎯 [EventBus] emit:', {
      id: eventWithId.id,
      type: event.type,
      userImageId: event.userImageId.substring(0, 8) + '...',
      payload: event.payload,
      source: event.source,
      timestamp: eventWithId.timestamp,
    });
  }

  // Aggiorna SharedValue - trigger automatico per tutti i listener
  eventSharedValue.value = eventWithId;
};